import type { AxiosError, AxiosResponse } from 'axios';

/** Grace period before releasing a download's blob URL, so the browser can commit the file. */
const REVOKE_DELAY_MS = 60_000;

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * `YYYY-MM-DD-HHmm` in the viewer's own timezone, for the download filename.
 *
 * Local time rather than a UTC `toISOString()` slice: the minute is what stops
 * two same-day exports from being saved as "staff-….xlsx" and "staff-… (1).xlsx",
 * indistinguishable on disk, and it has to read as the wall-clock time the person
 * clicked Export for that to help them.
 */
export function localFileStamp(d = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}`;
}

/**
 * Awaits an .xlsx request made with `responseType: 'blob'` and saves the body as
 * `filename`.
 *
 * A failed blob request carries the backend's JSON error envelope as a Blob, so
 * getErrorMessage would find no message and every failure — a 403 included —
 * would read "Something went wrong". The envelope is parsed back to JSON before
 * the error is rethrown.
 */
export async function downloadXlsx(request: Promise<AxiosResponse>, filename: string): Promise<void> {
  const res = await request.catch(async (e: unknown) => {
    const failed = (e as AxiosError).response;
    if (failed?.data instanceof Blob && failed.data.type.startsWith('application/json')) {
      failed.data = JSON.parse(await failed.data.text());
    }
    throw e;
  });
  saveXlsx(res.data, filename);
}

function saveXlsx(data: unknown, filename: string): void {
  const url = URL.createObjectURL(new Blob([data as BlobPart], { type: XLSX_MIME }));
  const a = document.createElement('a');
  a.href = url;
  // The browser uses this name, not the server's Content-Disposition, because
  // the href is a blob URL.
  a.download = filename;
  // anchor must be in the document for the click to register in some browsers
  document.body.appendChild(a);
  a.click();
  a.remove();
  // defer revocation so a slow browser still has the blob when it commits the file
  setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
}
